import { useState } from 'react';
import { Field, Callout } from './UI';

const UNSPSC_SUGGESTIONS = {
  'Professional Services':  { code: '80000000', desc: 'Management and Business Professionals and Administrative Services' },
  'Consulting':             { code: '80101500', desc: 'Business and Corporate Management Consultation Services' },
  'IT Services':            { code: '81110000', desc: 'Computer Services' },
  'Engineering':            { code: '81100000', desc: 'Engineering and Research and Technology Based Services' },
  'Environmental':          { code: '77100000', desc: 'Environmental Services' },
  'Legal':                  { code: '80130000', desc: 'Legal Services' },
  'Training':               { code: '86000000', desc: 'Education and Training Services' },
  'Construction':           { code: '72000000', desc: 'Building and Construction and Maintenance Services' },
  'Research':               { code: '80110000', desc: 'Market Research' },
  'Other':                  { code: '80000000', desc: 'Management and Business Professionals and Administrative Services' },
};

export default function CalEProcurePanel({ contract, updateC, profile, s215, apiKey, callAI, toast, fmtMoney }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const cal = contract.calEProcure || {};
  const upd = (k, v) => updateC({ calEProcure: { ...cal, [k]: v } });

  // Auto-suggest UNSPSC from category
  const suggested = UNSPSC_SUGGESTIONS[contract.category] || UNSPSC_SUGGESTIONS['Other'];

  async function suggestUnspsc() {
    setLoading(true);
    try {
      const sys = 'You are a California state procurement expert. Return ONLY a JSON object with exactly two fields: "code" (8-digit UNSPSC code as a string) and "desc" (short UNSPSC category description under 80 characters). No other text.';
      const text = await callAI(apiKey, {
        system: sys, model: 'claude-sonnet-4-6', max_tokens: 100,
        messages: [{ role: 'user', content: 'Contract: ' + contract.title + '\nCategory: ' + (contract.category || 'Professional Services') + '\nDescription: ' + (contract.description || '') + '\n\nSuggest the best UNSPSC code.' }],
      });
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      upd('unspsc', parsed.code);
      upd('unspscDesc', parsed.desc);
      toast.success('UNSPSC code suggested by Penny');
    } catch (e) {
      upd('unspsc', suggested.code);
      upd('unspscDesc', suggested.desc);
      toast.success('UNSPSC code populated from category');
    } finally { setLoading(false); }
  }

  // Build the full posting summary text
  const postingText = [
    'CALEPROCURE POSTING SUMMARY',
    '═'.repeat(40),
    '',
    'SOLICITATION TITLE: ' + (contract.title || '[CONTRACT TITLE]'),
    'PROCUREMENT METHOD: ' + (contract.procMethod || '[METHOD]'),
    'CONTRACT TYPE: ' + (contract.isIT === 'yes' ? 'IT Services' : 'Non-IT Services'),
    'ESTIMATED VALUE: ' + (fmtMoney(contract.budget) || '[BUDGET]'),
    '',
    'UNSPSC CODE: ' + (cal.unspsc || suggested.code),
    'UNSPSC DESCRIPTION: ' + (cal.unspscDesc || suggested.desc),
    '',
    'POSTING PERIOD: ' + (cal.postingDays || '10') + ' working days',
    'POSTING START DATE: ' + (cal.postingStart || '[DATE]'),
    'BID DUE DATE: ' + (cal.bidDueDate || '[DATE]'),
    '',
    'AGENCY: ' + (profile?.agencyName || 'Office of Energy Infrastructure Safety'),
    'DIVISION: ' + (profile?.agencyDivision || 'Admin Services Division'),
    '',
    'CONTACT NAME: ' + (profile?.contactName || '[NAME]'),
    'CONTACT TITLE: ' + (profile?.contactTitle || '[TITLE]'),
    'CONTACT PHONE: ' + (profile?.contactPhone || '[PHONE]'),
    'CONTACT EMAIL: ' + (profile?.contactEmail || '[EMAIL]'),
    '',
    'SMALL BUSINESS PREFERENCE: Yes — 5% preference applies',
    'DVBE INCENTIVE: Yes — DVBE incentive applies',
    '',
    'DESCRIPTION:',
    (contract.description || '[Contract description — paste from solicitation document]'),
    '',
    'NOTE: Verify all fields before posting. Solicitation must be posted for minimum 10',
    'working days per GC §14827.2. Do not release solicitation prior to CSCR publication.',
  ].join('\n');

  function copyToClipboard() {
    navigator.clipboard.writeText(postingText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success('CalEProcure posting summary copied to clipboard');
    });
  }

  return (
    <div className="card" style={{ borderLeft: '3px solid var(--teal-500)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 className="card-title" style={{ marginBottom: 4 }}>📋 CalEProcure Posting Summary</h3>
          <p style={{ fontSize: 12.5, color: 'var(--slate-500)' }}>
            All required CalEProcure fields pre-populated from your contract. Review, complete any blanks, then copy and paste into CalEProcure.
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => updateC({ showCalEProcure: false })}>✕ Close</button>
      </div>

      <Callout type="blue" style={{ marginBottom: 16 }}>
        Post to CalEProcure at <strong>caleprocure.ca.gov</strong> → Solicitations → New Solicitation. Minimum advertisement period is <strong>10 working days</strong> per GC §14827.2.
      </Callout>

      {/* Editable fields */}
      <div className="form-grid" style={{ marginBottom: 16 }}>
        {/* UNSPSC */}
        <div className="form-field">
          <label>UNSPSC Code</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={cal.unspsc || suggested.code}
              onChange={e => upd('unspsc', e.target.value)}
              placeholder="e.g. 80101500"
              style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, fontFamily: 'monospace' }}
            />
            <button className="btn btn-ghost btn-sm" disabled={loading} onClick={suggestUnspsc}>
              {loading ? <div className="spin spin-sm" /> : '✦ Suggest'}
            </button>
          </div>
          {(cal.unspscDesc || suggested.desc) && (
            <div style={{ fontSize: 11, color: 'var(--teal-600)', marginTop: 4 }}>
              {cal.unspscDesc || suggested.desc}
            </div>
          )}
        </div>

        {/* Posting days */}
        <div className="form-field">
          <label>Posting Period (working days)</label>
          <select
            value={cal.postingDays || '10'}
            onChange={e => upd('postingDays', e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
          >
            <option value="10">10 working days (minimum)</option>
            <option value="15">15 working days</option>
            <option value="20">20 working days</option>
            <option value="30">30 working days</option>
          </select>
        </div>

        <Field label="Posting Start Date" value={cal.postingStart || ''} onChange={v => upd('postingStart', v)} type="date" />
        <Field label="Bid Due Date" value={cal.bidDueDate || ''} onChange={v => upd('bidDueDate', v)} type="date" />
      </div>

      {/* Preview */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--slate-400)', marginBottom: 8 }}>
          Posting Summary Preview
        </div>
        <pre style={{
          background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
          borderRadius: 'var(--r-md)', padding: '14px 16px', fontSize: 11.5,
          fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: 'var(--slate-700)',
          maxHeight: 300, overflowY: 'auto', lineHeight: 1.7,
        }}>
          {postingText}
        </pre>
      </div>

      <button className="btn btn-gold" onClick={copyToClipboard}>
        {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
      </button>
    </div>
  );
}
